import { useMemo } from 'react';
import { useCustomerType } from '@salesforce/commerce-sdk-react';
import { useQuery } from '@tanstack/react-query';
import { defineMessages } from 'react-intl';
import type { UseQueryResult } from '@tanstack/react-query';
import { PageUrl } from '../../../../pages/utils/page-url';
import { useSLTConfig } from '../../../../slt/config/SLTConfigProvider';
import { QueryKey, rawUrlHash, useCustomPreferenceOrDefault, useUrl } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { getLinksUrls } from '../utils/getLinksUrls';
import { links as linksToRetrieve } from './links';
import type { FooterLink, LinkGroupProps, ResolvedFooterLink } from '../types';

export function useCustomerServiceLinks(): UseQueryResult<LinkGroupProps> {
    const { urlMap, absUrlMap } = useSLTConfig();
    const { isRegistered } = useCustomerType();

    // TODO: Remove this once the PWA Account redirect is in production
    // https://dbuy-fi.atlassian.net/browse/SLTBE-5781
    const pwaAccountRedirect = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ACCOUNT_REDIRECT,
        true,
    );
    const ordersHistoryUrl = useUrl(PageUrl.ORDERS_HISTORY);
    const allUrlsFound = useMemo(
        () => linksToRetrieve.every((link) => rawUrlHash(link.url) in urlMap),
        [urlMap],
    );
    return useQuery({
        queryKey: [QueryKey.CUSTOMER_SERVICE_LINKS, isRegistered],
        initialData: {
            title: messages.customerServiceTitle,
            containerDataTestId: 'footer-customer-service-links-container',
            headerDataTestId: 'footer-customer-service-links-header',
            links: footerLinks.map((fl) => {
                const linkToRetrieve = linksToRetrieve.find((ltr) => ltr.id === fl.id);

                // TODO: Remove this once the PWA Account redirect is in production
                // https://dbuy-fi.atlassian.net/browse/SLTBE-5781
                if (!!pwaAccountRedirect && fl.id === 2) {
                    return {
                        ...fl,
                        url: isRegistered ? ordersHistoryUrl : PageUrl.TRACK_ORDER,
                        isPwaLink: true,
                    };
                }

                return {
                    ...fl,
                    url:
                        fl.url ??
                        (linkToRetrieve?.url
                            ? (urlMap[rawUrlHash(linkToRetrieve?.url)] ??
                              absUrlMap[rawUrlHash(linkToRetrieve?.url)] ??
                              '')
                            : ''),
                    isPwaLink: false,
                };
            }),
        },
        queryFn: async () => {
            const resolvedLinksMap = await getLinksUrls(
                { ...urlMap, ...absUrlMap },
                linksToRetrieve,
            );

            const links: ResolvedFooterLink[] = footerLinks.map((fl) => {
                // TODO: Remove this once the PWA Account redirect is in production
                // https://dbuy-fi.atlassian.net/browse/SLTBE-5781
                if (!!pwaAccountRedirect && fl.id === 2) {
                    return {
                        ...fl,
                        url: isRegistered ? ordersHistoryUrl : PageUrl.TRACK_ORDER,
                        isPwaLink: true,
                    };
                }
                return {
                    ...fl,
                    url: fl.url ?? resolvedLinksMap.get(fl.id) ?? '',
                    isPwaLink: false,
                };
            });

            return {
                title: messages.customerServiceTitle,
                links,
                containerDataTestId: 'footer-customer-service-links-container',
                headerDataTestId: 'footer-customer-service-links-header',
            };
        },
        retryOnMount: !allUrlsFound,
    });
}

const messages = defineMessages({
    customerServiceTitle: {
        id: 'footer.customer_service.link_title',
        defaultMessage: 'Customer Service',
    },
    contactUsTitle: {
        id: 'footer.customer_service.contact_us_title',
        defaultMessage: 'Go to Contact Us',
    },
    contactUsLabel: {
        id: 'footer.customer_service.contact_us_label',
        defaultMessage: 'Contact Us',
    },
    orderStatusTitle: {
        id: 'footer.customer_service.order_status_title',
        defaultMessage: 'CHECK ORDER STATUS',
    },
    orderStatusLabel: {
        id: 'footer.customer_service.order_status_label',
        defaultMessage: 'Check Order Status',
    },
    shippingAndPickupTitle: {
        id: 'footer.customer_service.shipping_and_pickup_title',
        defaultMessage: 'SHIPPING & PICKUP',
    },
    shippingAndPickupLabel: {
        id: 'footer.customer_service.shipping_and_pickup_label',
        defaultMessage: 'Shipping & Pickup',
    },
    returnsAndExchangesTitle: {
        id: 'footer.customer_service.returns_and_exchanges_title',
        defaultMessage: 'RETURNS & EXCHANGES',
    },
    returnsAndExchangesLabel: {
        id: 'footer.customer_service.returns_and_exchanges_label',
        defaultMessage: 'Returns & Exchanges',
    },
    guaranteeTitle: {
        id: 'footer.customer_service.guarantee_title',
        defaultMessage: 'GUARANTEE',
    },
    guaranteeLabel: {
        id: 'footer.customer_service.guarantee_label',
        defaultMessage: 'Guarantee',
    },
    protectionPlansTitle: {
        id: 'footer.customer_service.protection_plans_title',
        defaultMessage: 'PROTECTION PLANS',
    },
    protectionPlansLabel: {
        id: 'footer.customer_service.protection_plans_label',
        defaultMessage: 'Protection plans',
    },
    accessibilityTitle: {
        id: 'footer.customer_service.accessibility_title',
        defaultMessage: 'ACCESSIBILITY',
    },
    accessibilityLabel: {
        id: 'footer.customer_service.accessibility_label',
        defaultMessage: 'Accessibility',
    },
    faqsTitle: {
        id: 'footer.customer_service.faqs_title',
        defaultMessage: 'FAQS',
    },
    faqsLabel: {
        id: 'footer.customer_service.faqs_label',
        defaultMessage: 'FAQs',
    },
    cookingClassFaqsTitle: {
        id: 'footer.customer_service.cooking_class_faqs_title',
        defaultMessage: 'Cooking Class FAQS',
    },
    cookingClassFaqsLabel: {
        id: 'footer.customer_service.cooking_class_faqs_label',
        defaultMessage: 'Cooking Class FAQs',
    },
    siteFeedbackTitle: {
        id: 'footer.customer_service.site_feedback_title',
        defaultMessage: 'SITE FEEDBACK',
    },
    siteFeedbackLabel: {
        id: 'footer.customer_service.site_feedback_label',
        defaultMessage: 'Site Feedback',
    },
    termsAndConditionsTitle: {
        id: 'footer.customer_service.terms_and_conditions_title',
        defaultMessage: 'Terms & Conditions',
    },
    termsAndConditionsLabel: {
        id: 'footer.customer_service.terms_and_conditions_label',
        defaultMessage: 'Terms & Conditions',
    },
});

const footerLinks: FooterLink[] = [
    {
        id: 1,
        title: messages.contactUsTitle,
        label: messages.contactUsLabel,
        dataTestId: 'footer.customer_service.contact_us',
    },
    {
        id: 2,
        title: messages.orderStatusTitle,
        label: messages.orderStatusLabel,
        dataTestId: 'footer.customer_service.order_status',
    },
    {
        id: 3,
        title: messages.shippingAndPickupTitle,
        label: messages.shippingAndPickupLabel,
        dataTestId: 'footer.customer_service.shipping_and_pickup',
    },
    {
        id: 4,
        title: messages.returnsAndExchangesTitle,
        label: messages.returnsAndExchangesLabel,
        dataTestId: 'footer.customer_service.returns_and_exchanges',
    },
    {
        id: 5,
        title: messages.guaranteeTitle,
        label: messages.guaranteeLabel,
        dataTestId: 'footer.customer_service.guarantee',
    },
    {
        id: 6,
        title: messages.protectionPlansTitle,
        label: messages.protectionPlansLabel,
        dataTestId: 'footer.customer_service.protection_plans',
    },
    {
        id: 7,
        title: messages.accessibilityTitle,
        label: messages.accessibilityLabel,
        dataTestId: 'footer.customer_service.accessibility',
    },
    {
        id: 8,
        title: messages.faqsTitle,
        label: messages.faqsLabel,
        dataTestId: 'footer.customer_service.faqs',
    },
    {
        id: 9,
        title: messages.cookingClassFaqsTitle,
        label: messages.cookingClassFaqsLabel,
        dataTestId: 'footer.customer_service.cooking_class_faqs',
    },
    {
        id: 10,
        title: messages.siteFeedbackTitle,
        label: messages.siteFeedbackLabel,
        dataTestId: 'footer.customer_service.site_feedback',
        componentId: 'siteSurveyFooter', // <a> ID used in GTM by Opinion Lab tag
    },
    {
        id: 11,
        title: messages.termsAndConditionsTitle,
        label: messages.termsAndConditionsLabel,
        dataTestId: 'footer.customer_service.terms_and_conditions',
    },
];
